import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useMutation } from 'react-query';
import { useParams, useHistory, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Container, Card, CardBody, Button, Row, Col, Spinner } from 'reactstrap';
import { FormProvider, useForm, useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import clsx from 'clsx';

import HorizontalStepper from '../../components/HorizontalStepper';
import ConfirmationModal from '../../components/ConfirmationModal';
import { createTestament } from '../../api/actions/contracts';
import { fetcher } from '../../api/client';
import { useAuthState } from '../../context/auth';
import { useEditorState } from '../../context/editor';
import {useLocalStorage} from '../../hooks/useLocalStorage';

import {
  TestamentForm,
  TestamentFormSchema,
  testamentInitialValues,
  witnessInitialValue,
  heirInitialValue,
  legateeInitialValue,
  executorInitialValue
} from './TestamentForms';
import { yupResolver } from '@hookform/resolvers/yup';

export function TestamentConfiguration({ className, templateId, onTestamentSent }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const { publicTemplate } = useEditorState();
  const { t } = useTranslation()
  const [data, setData] = useLocalStorage(`testament-${templateId}`,{ ... testamentInitialValues, executorHeir: 'none' });
  const [isWarningAccepted, setIsWarningAccepted] = useLocalStorage(`testament-${templateId}-isWarningAccepted`, false);
  const history = useHistory();
  const auth = useAuthState();
  const methods = useForm({
    defaultValues: data,
    resolver: yupResolver(TestamentFormSchema)
  });
  const executors = methods.watch('executors')
  const steps = useMemo(() => {
    return [
      {
        title: 'Testador',
        key: 'testator',
        description: 'El Testador es la persona que hace testamento para decidir lo que sucederá con sus bienes tras su fallecimiento',
        onClick: () => {}
      },
      {
        title: 'Heredero(s)',
        key: 'heirs',
        description: 'Persona que recibe los bienes, derechos y obligaciones de una persona cuando esta muere, en cumplimiento de la ley o de las disposiciones señaladas en un testamento.',
        onClick: () => {}
      },
      {
        title: 'Legatario',
        key: 'legatees',
        description: 'El Legatario es un sucesor a título particular. Es decir, solo recibe los bienes del legado y no responde de las deudas del testador.',
        onClick:() => {}
      },
      {
        title: 'Albacea',
        key: 'executors',
        description: 'Persona encargada de hacer cumplir la última voluntad de un difunto y de custodiar sus bienes hasta que se repartan entre los herederos.',
        onClick: () => {}
      },
      {
        title: 'Testigo(s)',
        key: 'witnesses',
        description: 'Un testigo es la figura procesal invitada a declarar sobre la certeza de ciertos hechos investigados judicialmente.',
        onClick: () => {}
      },
    ]
  }, [])
  
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;
  const populatedStep = steps[currentStep] || {};

  const { mutate: createTestamentMutate, isLoading, isSuccess } = useMutation((payload) => fetcher(createTestament(payload)), {
    onSuccess: (result) => {
      // history.push(`/documents/${result.data._id}/edit`)
      onTestamentSent()
      setData({ ...testamentInitialValues, executorHeir: 'none' })
      setIsWarningAccepted(false)
    }
  })
  
  const handleNextStep = async () => {
    saveData(() => { 
      setCurrentStep(Math.min(currentStep + 1, steps.length))
      methods.reset(data)
    }, { ignoreValidation: false })
    console.log({data})
    
  }
  const handlePrevStep = () => {
    saveData(() => { 
      setCurrentStep(Math.max(currentStep - 1, 0))
      methods.reset(data)
    }, { ignoreValidation: true })
  }

  const handleHeirChange = (evt) => {
    const value = evt.target.value;
    if (value === 'none') {
      methods.setValue('executors[0].name', '');
      methods.setValue('executors[0].last_name', '');
      methods.setValue('executors[0].email', '');
      return;
    } else {
      setData(currentData => ({...currentData, executorHeir: value }))
      const heir = data.heirs[value];
      methods.setValue('executors[0].name', heir.name);
      methods.setValue('executors[0].last_name', heir.last_name);
      methods.setValue('executors[0].email', heir.email);
    }
   
  }

  const saveData = async (cb = () => {}, { ignoreValidation } = {}) => {
    let errors = {} ;
    if (!ignoreValidation) {
      await methods.trigger(populatedStep.key);
      errors = methods.errors[populatedStep.key] || {};
    }
    const formData = methods.getValues();

    if (formData.heirs && !ignoreValidation) {
      const percentage = formData.heirs.reduce((total, heir) => {
        total += Number(heir.properties.percentageOfInheritanceState)
        return total;
      }, 0)
      if (percentage !== 100) {
        formData.heirs.map((heir, i) => {
          methods.setError(`heirs[${i}].properties.percentageOfInheritanceState`, {
            type: "manual",
            message: 'PercentageOfInheritanceStateError'
          })
        })
        return;
      }
    }
   

    const newData = {...data, ...formData}   
    
    if (currentStep === 2)  {
      newData.legatees = formData.legatees ? formData.legatees : [];
    }
    if (currentStep === 3)  {
      newData.executors = formData.executors ? formData.executors : [];
    }
    

    const hasErrors = Object.keys(errors).length > 0;
  
    if (hasErrors) toast.error('Alguno de los campos tiene un error', {
      toastId: 'field-errors'
    });
    if (!hasErrors)  cb(newData);

    setData(newData)
  }
  const handleCreateTestament = () => {
    saveData((data) => {
      console.log({data})
      const payload = {
        templateId,
        ...data,
        legatee: data.legatees[0],
        executor: data.executors[0]
      }
      if (!publicTemplate?.userTemplate?._id) {
        payload.publicTemplateId = publicTemplate?._id;
      }
      createTestamentMutate(payload)
      // onTestamentSent()
    })
  }

  const handleConfirmTestament = () => {
    if (!auth.hasAccount) {
      return auth.openAuthModal({ type: 'login' })
    }
    saveData((data) => {
      setIsConfirmationOpen(true)
    }, {ignoreValidation: false})
  }

  const handleCancel = () => {
      setIsConfirmationOpen(false)
  }

  return (
    <FormProvider {...methods}>
    <div className={clsx("h-100 bg-light rounded-0 d-flex align-items-start flex-column", className)}>
      <div className="text-center px-4 pt-3">
        <h4 className="font-weight-bold">{currentStep + 1}. {populatedStep.title}</h4>
        <small className="text-muted">{populatedStep.description}</small>
        <HorizontalStepper 
          steps={steps} 
          size={24}
          checkIcon={<FontAwesomeIcon icon="check-circle" />}
          circleFontSize={11}
          activeStep={currentStep}   
          completeColor="#f47b33"
          completeBarColor="#f47b33"
          defaultTitleOpacity={0}
          completeTitleOpacity={0}
          activeTitleOpacity={0}
          completeOpacity={0.7}
          activeColor="#f47b33"
        />
      </div>
      <div className="w-100 mb-auto overflow-y-auto overflow-hidden-x">
        <ShouldShow show={currentStep === 0}>
          <TestamentForm 
            className="p-3"
            name="testator"
            initialValue={data.testator}
            errors={methods.errors.testator}
            fields={['name', 'lastName', 'email', 'nationality', 'curp', 'birthDate' , 'birthplace' ,'officialIdentificationNumber', 'maritalStatus', 'zipCode', 'street', 'exteriorNumber', 'interiorNumber', 'state', 'municipio']}
          />
        </ShouldShow>
        <ShouldShow show={currentStep === 1}>
          <HeirList  initialValue={data.heirs} />
        </ShouldShow>
        <ShouldShow show={currentStep === 2}>
          <LegateeList initialValue={data.legatees} />
        </ShouldShow>
        <ShouldShow show={currentStep === 3}>
          {!!executors?.length && 
            <>
              <label className="mx-3 font-weight-bold">Tomar información de:</label>
              <select className="form-control mx-3" defaultValue={data.executorHeir} onChange={handleHeirChange}>
                <option value="none">Ninguno</option>
                {data.heirs.map((heir, i) => (
                  <option value={i}>{heir.name} {heir.last_name} ({heir.email})</option>
                ))}
              </select>
            </>
          }
          <ExecutorList initialValue={data.executors} />
          {/* <TestamentForm 
            className="p-3"
            name="executor"
            initialValue={data.executor}
            errors={methods.errors.executor}
            fields={['name', 'lastName', 'email', 'curp', 'maritalStatus', 'zipCode', 'street', 'exteriorNumber', 'interiorNumber', 'state', 'municipio']}
          /> */}
        </ShouldShow>
        <ShouldShow show={currentStep === 4}>
          <div className="mx-3 text-muted">
            La ley exige que en el testamento privado se señalen 5 testigos durante la declaración del testador. 
            {/* Sin embargo, para casos de suma urgencia es posible señalar 3 testigos. */}
          </div>
          <WitnessList initialValue={data.witnesses} isLoading={isLoading} />
        </ShouldShow>
        
      </div>
      <div className="p-3 d-flex flex-row-reverse justify-content-between w-100 border-top">
        <Button hidden={isLastStep} color="accent-100" onClick={handleNextStep}>Siguiente</Button>
        <Button hidden={!isLastStep} color="accent-100" onClick={handleConfirmTestament}>Crear</Button>
        <Button hidden={isFirstStep} color="primary" outline onClick={handlePrevStep}>Anterior</Button>
      </div>
    </div> 
    <ConfirmationModal
        isOpen={isConfirmationOpen}
        hasSucceed={isSuccess}
        isLoading={isLoading}
        onConfirm={handleCreateTestament}
        confirmText={t('actions.send')}
        cancelText={t('actions.makeChanges')}
        question={t('¿Estás seguro que quieres enviar el testamento?')}
        onCancel={handleCancel}
    /> 
    <ConfirmationModal
        isOpen={!isWarningAccepted}
        onConfirm={() => setIsWarningAccepted(true)}
        confirmText={t('Confirmar')}
        QuestionComponent={TestamentWarningInfo}
        onCancel={() => { setIsWarningAccepted(false) }}
    /> 
    </FormProvider>
  )
}

function TestamentWarningInfo() {
  return (
    <div className="text-left" style={{fontSize: 12}}>
      <p>
        De acuerdo con lo establecido en el Código Civil Federal y demás códigos locales, para hacer 
        uso del Testamento Digital debes tomar en cuenta las siguientes consideraciones:
      </p>
      <ul>
        <li>Puede utilizarse solo cuando el testador sufre de una enfermedad tan grave que le impida acudir al notario a hacer su testamento. (ART. 1565)</li>
        <li>Únicamente será válido si el testador fallece por la causa por la que hizo su testamento por esa vía dentro del siguiente mes. (ART. 1571)</li>
        <li>Deben participar 5 testigos. En caso de extrema urgencia pueden acudir 3 testigos. (ARTS. 1567 y 1569)</li>
      </ul>
      <p>
        Por lo anterior, el Testamento Digital únicamente será válido frente a cualquier autoridad en caso de 
        concurrir los requisitos mencionados anteriormente. 
      </p>
      <p>
        TRATO no representa autoridad alguna, por lo que el Testamento Digital solo es una herramienta para 
        facilitar a las personas el acceso a la justicia en situaciones de urgencia y no sustituye la figura 
        del Testamento Público Abierto por lo que recomendamos que se formalicen los actos jurídicos realizados 
        a través del Testamento Digital.
      </p>
    </div>
  )
}

function ShouldShow({ show, children }) {
  if (!show) {
    return null;
  }

  return children
}

function HeirList({ initialValue }) {
  const { errors } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    name: 'heirs'
  })
  const minimumHeirs = 1;

  const handleAdd = () => {
    append(heirInitialValue)
  }

  const handleRemove = (index) => () => {
    remove(index)
  }

  return (
    <div>
      {
        fields.map((heir, i) => {
          return (
            <TestamentForm 
              className="p-3 border-bottom"
              name={`heirs[${i}]`}
              isInArray
              initialValue={initialValue[i] ?? heirInitialValue}
              errors={errors?.heirs?.[i]}
              fields={['name', 'lastName', 'email', 'percentageOfInheritanceState']}
              showRemove={fields.length > minimumHeirs}
              onRemove={handleRemove(i)}
            />
          )
        })
      }
      <div 
          className="mx-3 my-4 color-accent pointer" 
          onClick={handleAdd}
          // disabled={!isEditable}
      >
          + Agregar heredero(a)
      </div>
    </div>
    
  )
}

function LegateeList({ initialValue }) {
  const { errors } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    name: 'legatees'
  })  
  const maximumLegatees = 1;


  const handleAdd = () => {
    if (fields.length < maximumLegatees) {
      append(legateeInitialValue)
    }
  }

  const handleRemove = (index) => () => {
    remove(index)
  }


  return (
    <div>
      {
        fields.map((legatee, i) => {
          return (
            <TestamentForm 
              className="p-3 border-bottom"
              name={`legatees[${i}]`}
              isInArray
              errors={errors?.legatees?.[i]}
              initialValue={initialValue[i] ?? legateeInitialValue}
              fields={['name', 'lastName', 'email', 'curp', 'legacyDescription']}
              showRemove
              onRemove={handleRemove(i)}
            />
          )
        })
      }
      {
        fields.length < maximumLegatees &&
        <div className="mx-3 my-4">
          <div className="text-muted">
            Únicamente será necesario designar legatarios en el testamento cuando desees concederles la propiedad de un bien o derecho en específico (Bien inmueble, automóvil, etc...).
          </div>
          <div 
              className="m-2 color-accent pointer" 
              onClick={handleAdd}
              // disabled={!isEditable}
          >
              + Agregar legatario
          </div>
        </div>

      }
    </div>
    
  )
}

function ExecutorList({ initialValue }) {
  const { errors } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    name: 'executors'
  })  
  const maximumExecutors = 1;


  const handleAdd = () => {
    if (fields.length < maximumExecutors) {
      append(executorInitialValue)
    }
  }

  const handleRemove = (index) => () => {
    remove(index)
  }


  return (
    <div>
      {
        fields.map((executor, i) => {
          return (
            <TestamentForm 
              className="p-3 border-bottom"
              name={`executors[${i}]`}
              isInArray
              errors={errors?.executors?.[i]}
              initialValue={initialValue[i] ?? executorInitialValue}
              fields={['name', 'lastName', 'email', 'curp', 'maritalStatus', 'zipCode', 'street', 'exteriorNumber', 'interiorNumber', 'state', 'municipio']}
              showRemove
              onRemove={handleRemove(i)}
            />
          )
        })
      }
      {
        fields.length < maximumExecutors &&
        <div className="mx-3 my-4">
          <div 
              className="m-2 color-accent pointer" 
              onClick={handleAdd}
              // disabled={!isEditable}
          >
              + Agregar Albacea
          </div>
        </div>

      }
    </div>
    
  )
}
 
 
function WitnessList({ initialValue, isLoading }) {
  const { errors } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    name: 'witnesses'
  })  
  const minimumWitnesses = 5;
  const maximumWitnesses = 5;


  const handleAdd = () => {
    if (fields.length < maximumWitnesses) {
      append(witnessInitialValue)
    }
  }

  const handleRemove = (index) => () => {
    remove(index)
  }

  return (
    <div>
      {
        fields.map((witness, i) => {
          return (
            <TestamentForm 
              className="p-3 border-bottom"
              name={`witnesses[${i}]`}
              isInArray
              isLoading={isLoading}
              errors={errors?.witnesses?.[i]}
              initialValue={initialValue[i] ?? witnessInitialValue}
              fields={['name', 'lastName', 'email', 'curp', 'maritalStatus', 'birthDate', 'nationality', 'zipCode', 'street', 'exteriorNumber', 'interiorNumber', 'state', 'municipio']}
              showRemove={fields.length > minimumWitnesses}
              onRemove={handleRemove(i)}
            />
          )
        })
      }
      {
        fields.length < maximumWitnesses &&
        <div 
            className="mx-3 my-4 color-accent pointer" 
            onClick={handleAdd}
            disabled={isLoading}
        >
            + Agregar testigo
        </div>
      }
    </div>
    
  )
}